/* eslint-disable prefer-const */
import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";

import { styled } from "@mui/material/styles";
import {
  useNavigate,
  Outlet,
  useParams,
  Params,
  useLocation,
} from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import clsx from "clsx";
import color from "color";
import { motion } from "framer-motion";

import times from "lodash/times";
import reduce from "lodash/reduce";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import useSetState from "react-use/lib/useSetState";
import useClickAway from "react-use/lib/useClickAway";

import useIdleEffect from "../../lib/hooks/useIdleEffect";
import { useClientPlan, useLayout } from "../../lib/hooks/useQueryBoard";

// import useEventTracking from "../../bit/tracking/useEventTracking";
// import usePageTracking from "../../bit/tracking/usePageTracking";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";
import OfflineModal from "../../components/Offline/OfflineModal";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../components/Aggrement/hooks/useAgreementState";

import { isVirtualKeyboardEnabled, isInWebView } from "../../config/const";
import { useOfflineContext } from "../../context/Offline";
import Reveal from "../../components/Animations/Reveal";
import Bump from "../../components/Animations/Bump";
import MoveUp from "../../components/Animations/MoveUp";

const Divider = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
}));

const ScannerButton = styled(Button)(() => ({
  fontSize: "2.5rem",
}));

export const PinInput = styled(motion.input)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderColor: theme.palette.primary.main,
  background: color(theme.palette.background.default).lighten(0.1).hex(),

  "&:focus": {
    borderColor: theme.palette.primary.main,
    backgroundColor: color(theme.palette.background.default).darken(0.07).hex(),
  },

  "&.error": {
    borderColor: theme.palette.error.main,
  },
}));

interface IQueryParams extends Params {
  action: "checkin" | "checkout";
  code: string;
}

const defaultPinCode = {
  1: "",
  2: "",
  3: "",
  4: "",
  5: "",
  6: "",
};

const isMediaDevicesSupported = () => {
  const isMediaDevicesSupported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices;

  return isMediaDevicesSupported;
};

const CheckPage = () => {
  const { t } = useTranslation(["check"]);

  const { action, code } = useParams() as IQueryParams;

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/access\/${action}\/?$`).test(pathname);

  const clientPlan = useClientPlan();
  const { online } = useOfflineContext();

  const isCameraFeatureEnabled = useMemo(() => {
    const isNotFree = clientPlan && clientPlan !== "free";

    if (!isNotFree) {
      return false;
    }

    return isMediaDevicesSupported();
  }, [clientPlan]);

  const { integratedManualCheckin, checkout, manualCheckout } = useLayout();

  const [pinCode, setPinCode] = useSetState(defaultPinCode);

  const [focusedInput, setFocusedInput] = useState<undefined | string>("1");

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { showFirst, unacceptedAgreements } = useAgreementState();

  const [pendingAgreements, setPendingAgreements] = useState<
    IEntranceAgreementFile[]
  >(
    action === "checkin" && showFirst && mainRoute && unacceptedAgreements
      ? unacceptedAgreements
      : [],
  );

  // usePageTracking({
  //   page: action,
  // });

  // const { trackEvent } = useEventTracking();

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const onKeyboardChange = useCallback(
    (value: string) => {
      const regex = /^\d$/;

      if (
        focusedInput &&
        +focusedInput < 7 &&
        (value === "" || regex.test(value))
      ) {
        /**
         * Reset virtual input after we set the code
         */
        if (keyboard.current) {
          keyboard.current.setInput("", focusedInput);
        }

        setPinCode({ [focusedInput]: value });

        if (focusedInput === "6" && value.length) {
          setFocusedInput(undefined);
        } else {
          if (document.activeElement) {
            (document.activeElement as HTMLInputElement)?.blur();
          }

          setFocusedInput(`${+focusedInput + 1}`);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button: string) => {
      if (button === "{bksp}" && typeof focusedInput !== "undefined") {
        setPinCode(() => ({ [focusedInput]: "" }));

        const nextFocusedInput = Math.max(1, +focusedInput - 1);

        setFocusedInput(`${nextFocusedInput}`);
      }
    },
    [focusedInput],
  );

  const onPasteCapture = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const content = event.clipboardData.getData("Text");

      const isValidPin = /^[0-9]{1,6}$/g.test(content);

      if (!isValidPin) {
        return;
      }

      event.preventDefault();

      const pinCode = reduce(
        times(6),
        (acc, key) => ({
          ...acc,
          [key + 1]: content[key],
        }),
        {},
      );

      setPinCode(pinCode);
    },
    [],
  );

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      const {
        target: { name },
      } = event;
      // trackEvent({ type: "input_focus", value: `pin_${action}` });
      setFocusedInput(`${+name}`);

      if (keyboard.current) {
        keyboard.current.setInput("", name);
      }
    },
    [],
  );

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;

    /**
     * Blur when setting last input is set form keyboard
     */
    if (name === "6" && value && !!value.length) {
      event.target.blur();
    }

    const regex = /^\d$/;

    if (value === "" || regex.test(value)) {
      setPinCode({ [name]: value });

      let nextFocusedInput: number | undefined = +name;

      if (name === "6") {
        nextFocusedInput = undefined;
      } else if (value.length) {
        nextFocusedInput += 1;
      } else {
        nextFocusedInput -= 1;
      }

      setFocusedInput(nextFocusedInput ? `${nextFocusedInput}` : undefined);
    }
  }, []);

  const onBeforeInput = useCallback((event: InputEvent) => {
    switch (event.inputType) {
      case "deleteByComposition":
      case "deleteByCut":
      case "deleteByDrag":
      case "deleteEntireSoftLine":
      case "deleteSoftLineBackward":
      case "deleteSoftLineForward":
      case "deleteHardLineBackward":
      case "deleteHardLineForward":
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteContent":
      case "deleteContentForward":
      case "deleteContentBackward": {
        event.preventDefault();

        // eslint-disable-next-line prefer-destructuring
        const name = (event?.currentTarget as HTMLInputElement).name;

        setPinCode({ [name]: "" });

        const nextFocusedInput = Math.max(1, +name - 1);

        setFocusedInput(`${nextFocusedInput}`);

        break;
      }

      default: {
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (!isInWebView) {
      return;
    }

    const ids = Object.keys(defaultPinCode);

    ids.forEach((id) => {
      const element = document.querySelector<HTMLInputElement>(`[id='${id}']`);

      element?.addEventListener("beforeinput", onBeforeInput);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      ids.forEach((id) => {
        const element = document.querySelector<HTMLInputElement>(
          `[id='${id}']`,
        );

        element?.removeEventListener("beforeinput", onBeforeInput);
      });
    };
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const {
        code,
        currentTarget: { name },
      } = event;

      switch (code) {
        case "Backspace": {
          event.preventDefault();

          setPinCode({ [name]: "" });

          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowRight": {
          event.preventDefault();

          const nextFocusedInput = Math.min(+name + 1, 6);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowUp":
        case "ArrowDown": {
          event.preventDefault();

          break;
        }

        default: {
          break;
        }
      }
    },
    [],
  );

  useClickAway(keyboardBox, (event) => {
    if (event.target && (event.target as HTMLElement)?.tagName === "INPUT") {
      return;
    }

    setFocusedInput(undefined);
  });

  /**
   * Auto-focus input
   */
  useEffect(() => {
    if (focusedInput) {
      const input = document.querySelector<HTMLInputElement>(
        `input[name="${focusedInput}"]`,
      );

      if (input && typeof input.focus === "function") {
        input.focus();
        input.select();
      }
    }
  }, [focusedInput]);

  /**
   * Re-focus first input after we go back to this page.
   * Useful when we get back from code scan with an error
   */
  useEffect(() => {
    if (!code && !focusedInput) {
      setFocusedInput("1");
    }
  }, [code]);

  useEffect(() => {
    const pinCodeArr = Object.values(pinCode);

    const timeout = setTimeout(() => {
      if (pinCodeArr.every((input) => input !== "")) {
        navigate({
          pathname: `${pinCodeArr.join("")}`,
        });

        // setPinCode(defaultPinCode);
        setFocusedInput(undefined);

        if (document.activeElement) {
          (document.activeElement as HTMLInputElement)?.blur();
        }
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [pinCode]);

  const backLink = useMemo(() => {
    if (action === "checkin" && integratedManualCheckin?.enabled) {
      return "/access/option/checkin";
    }

    if (action === "checkout" && checkout?.enabled && manualCheckout?.enabled) {
      return "/access/option/checkout";
    }

    return "/";
  }, []);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to={backLink} />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1">
            <Trans
              i18nKey={
                isCameraFeatureEnabled
                  ? `check:qrScanner.${action}.title`
                  : "check:title"
              }
            />
          </Typography>
        </Title>
        <div className="check-options">
          {isCameraFeatureEnabled && (
            <>
              <Bump disabled={!mainRoute}>
                <motion.div>
                  <ScannerButton
                    variant="contained"
                    disabled={!online}
                    onClick={() => {
                      // trackEvent({ type: "scan_button_click", value: action });
                      navigate(`/scanner/${action}`);
                    }}
                  >
                    {t("check:qrScanner.btn")}
                  </ScannerButton>
                </motion.div>
              </Bump>

              <div className="divider d-flex align-items-center mx-auto">
                <Divider className="col dropdown-divider" />
                <Typography variant="h4" className="col-auto px-5">
                  {t("check:orText")}
                </Typography>
                <Divider className="col dropdown-divider" />
              </div>
            </>
          )}
          <form className="pin__wrapper" autoComplete="off">
            <Reveal className="mx-auto" disabled={!mainRoute}>
              <Typography
                variant="h4"
                className="font-weight-regular pin__title w-100"
              >
                {t("check:pinCode")}
              </Typography>
            </Reveal>
            {([1, 2, 3, 4, 5, 6] as const).map((i) => (
              <MoveUp key={i} disabled={!mainRoute} delay={0.15 + 0.05 * i}>
                <PinInput
                  id={`${i}`}
                  name={`${i}`}
                  min={0}
                  max={9}
                  type={isVirtualKeyboardEnabled ? "text" : "number"}
                  className="pin__input"
                  autoComplete="chrome-off"
                  onFocus={onFocus}
                  onChange={onChange}
                  onKeyDown={onKeyDown}
                  onPasteCapture={onPasteCapture}
                  value={pinCode[i]}
                  maxLength={1}
                  pattern="[0-9]*"
                />
              </MoveUp>
            ))}
          </form>
        </div>
        {isVirtualKeyboardEnabled && (
          <div
            ref={keyboardBox}
            className={clsx("keyboard ", { keyboard__out: !focusedInput })}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <VirtualKeyboard
              ref={keyboard}
              disableButtonHold
              layoutName="pin"
              inputName={focusedInput}
              onChange={onKeyboardChange}
              onKeyPress={onKeyboardKeyPress}
            />
          </div>
        )}
      </div>
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => {
          navigate("/");
        }}
        onSubmit={() => {
          setPendingAgreements((prev) => {
            const [_accepted, ...rest] = prev;

            return rest;
          });
        }}
      />
      <Outlet />
      <OfflineModal />
    </Curve>
  );
};

export default CheckPage;
